import { Avatar, Box, Card, CardContent, IconButton, Stack } from '@mui/material'
import { hexWithOpacity, toTitleCase } from 'common/utils'
import CustomText from 'components/common/CustomText/CustomText'
import { colors } from 'styles/colors'
import { EInviteStatus } from 'enum/EInviteStatus'
import useGlobalStyles from 'styles/globalStyles'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import { useAcceptInvitation } from 'hooks/useUsers'
import { useAuth } from 'context/AuthContext'
import { useApproveProcedureInvitation, useCreateProcedure, useRejectProcedureInvitation } from 'hooks/useProcedure'
import { snackbarService } from 'services/snackbarService'

interface Props {
  item: any;
  refetch?: () => void;
}
export default function NotificationCard({ item, refetch }: Props) {
  const { user } = useAuth();
  const globalStyles = useGlobalStyles();

  const { mutate: createMutation, isLoading: isCreating } = useCreateProcedure(
    (data: any) => {
      if (data.status_code == 200) {
        approveMutation(item.id);
      } else {
        snackbarService.showSnackbar(data?.data ?? 'Could not approve invitation, please try again', 'error');
      }
    },
    (error: any) => {
      snackbarService.showSnackbar(error?.message, 'error');
    }
  )

  const { mutate: approveMutation, isLoading: isAccepting } = useApproveProcedureInvitation(
    (data: any) => {
      if (data.status_code == 200) {
        snackbarService.showSnackbar('Invitation has been approved successfully', 'success');
        refetch?.();
      } else {
        snackbarService.showSnackbar(data?.data ?? 'Could not approve invitation, please try again', 'error');
      }
    },
    (error: any) => {
      snackbarService.showSnackbar(error?.message, 'error');
    }
  );

  const { mutate: rejectMutation, isLoading: isRejecting } = useRejectProcedureInvitation(
    (data: any) => {
      if (data.status_code == 200) {
        snackbarService.showSnackbar('Invitation has been rejected successfully', 'success');
        refetch?.();
      } else {
        snackbarService.showSnackbar(data?.data ?? 'Could not reject invitation, please try again', 'error');
      }
    },
    (error: any) => {
      snackbarService.showSnackbar(error?.message, 'error');
    }
  )

  const handleAccept = () => {
    createMutation({
      userId: user.id,
      data: item.payload,
      approve: true,
    });
  }

  const handleReject = () => {
    rejectMutation(item.id);
  }

  return (
    <Card
      key={item.id}
      sx={{
        borderRadius: 3,
        boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
        minHeight: 180,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent
        sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top row */}
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1}
          >
            <Avatar
              src={item?.user_media?.href_small}
              alt={`profile image`}
              sx={{
                width: 38,
                height: 38,
                bgcolor: colors.avatarBg,
              }}
            />

            <CustomText
              text={`${item.inviter.last_name}, ${item.inviter.first_name}`}
              color={colors.primary}
              fontSize={18}
              fontWeight={600}
              noWrap />
          </Stack>

          {/* User name comes here */}
        </Box>

        <Box mt={1}>
          <CustomText
            text={`You have been added as a ${item.invitee_type} by ${item?.inviter?.last_name}, ${item?.inviter?.first_name}. Approve to add the case to your list.`}
            color={colors.secondary}
            fontSize={15}
          />
        </Box>

        <Box mt={'auto'}>
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={0.5}
          >
            <CustomButton
              key={'reject'}
              variant='outlined'
              intent='error'
              text='Reject'
              onClick={handleReject}
              fullWidth
              height={35}
              disabled={isRejecting || isAccepting || isCreating}
              loading={isRejecting}
            />
            <CustomButton
              key={'add'}
              variant='filled'
              color='success'
              text={'Accept'}
              onClick={handleAccept}
              fullWidth
              height={35}
              disabled={isRejecting || isAccepting || isCreating}
              loading={isAccepting || isCreating}
            />
          </Stack>
        </Box>
      </CardContent>
    </Card>
  )
}